// =============================================================================
// pageStore — a tiny module-level state store, read via useSyncExternalStore.
//
// Why this exists: pages unmount on react-router navigation (App renders them
// through <Outlet/>), so per-page component state is destroyed on navigate-away
// and rebuilt on return. State that should survive a tab-switch (staged edits,
// filters, column layout, sort, scroll, chat, etc.) lives HERE instead — module
// scope, not the React tree, so it is untouched by unmount.
//
// Session-scoped, in-memory. No new dependency — React 18's useSyncExternalStore
// is the sanctioned primitive for an external store.
// =============================================================================

// The one shared state object. Slices are added as top-level keys — one per page,
// each holding that page's session-scoped view state so it survives navigation.
export interface PageState {
  sites: SitesSliceState
  askPortal: AskPortalSliceState
  intelligence: IntelligenceSliceState
  dashboard: DashboardSliceState
  charting: ChartingSliceState
  persona: PersonaSliceState
}

// The active "user view" persona (top-bar dropdown). NOT per-page — it's a global identity
// that scopes the Sites list (ownerScope) and tags edits (name = X-Actor-Name). The roster
// itself is server-owned (meta.personas, single-sourced from the site_owner_pm enum); this
// slice just holds whichever one is active. `Persona` is defined in lib/types (server shape).
import type { Persona } from '../lib/types'
export type { Persona }
export interface PersonaSliceState {
  persona: Persona
}

// The default active persona before /api/meta loads: 'Whole Estate' (unscoped). Mirrors
// meta.personas[0]; kept minimal here only as the pre-load seed, replaced once meta arrives.
export const DEFAULT_PERSONA: Persona = { id: 0, name: 'Whole Estate', role: 'Programme View', ownerScope: null }

// AI Chat: the conversation. `input`/`loading` stay local (truly transient).
export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  tools?: string[]
  feedback?: boolean | null
}
export interface AskPortalSliceState {
  messages: ChatMessage[]
}

// Intelligence: the generated NBA result + the cards-expanded toggle. `nba` holds the
// AgentNextBestAction payload (typed at the page; kept `unknown` here to avoid a store→page
// type dependency).
export interface IntelligenceSliceState {
  nba: unknown   // AgentNextBestAction payload or null (unknown subsumes null)
  cardsExpanded: boolean
}

// Dashboard: which KPI row is selected in the performance table (drives the paired chart).
export interface DashboardSliceState {
  selectedKpi: string
}

// AI Charting: the last built chart (VisualiseResponse) + the prompt that produced it.
export interface ChartingSliceState {
  lastChart: unknown   // VisualiseResponse payload or null (unknown subsumes null)
  lastPrompt: string
}

// The Sites page slice. `pendingEdits` is the staged-but-unapplied buffer:
// rowId -> field -> staged value (a per-cell diff from the server row).
export interface SitesSliceState {
  initialized: boolean
  pendingEdits: Record<number, Record<string, unknown>>
  colFilters: Record<string, string[]>
  order: string[]
  hidden: string[]
  sort: string
  dir: 'asc' | 'desc'
  search: string
  slicer: string
  scroll: number
}

const initialSitesSlice: SitesSliceState = {
  initialized: false,
  pendingEdits: {},
  colFilters: {},
  order: [],
  hidden: [],
  sort: 'site_id',
  dir: 'asc',
  search: '',
  slicer: '',
  scroll: 0,
}

let state: PageState = {
  sites: initialSitesSlice,
  askPortal: { messages: [] },
  intelligence: { nba: null, cardsExpanded: true },
  dashboard: { selectedKpi: 'bb1_ordered' },
  charting: { lastChart: null, lastPrompt: '' },
  persona: { persona: DEFAULT_PERSONA },
}

const listeners = new Set<() => void>()

export function getState(): PageState {
  return state
}

// Server snapshot must be referentially stable (SSR / hydration guard). We never
// SSR this app, but useSyncExternalStore requires it — return the live state.
export function getServerSnapshot(): PageState {
  return state
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

// Replace state via an updater and notify subscribers. Callers return a NEW
// state object (immutable update) so useSyncExternalStore selectors detect change.
export function update(fn: (prev: PageState) => PageState): void {
  const next = fn(state)
  if (next === state) return
  state = next
  listeners.forEach((l) => l())
}

// Generic single-slice updater: apply fn to one top-level slice, no-op if unchanged.
export function updateSlice<K extends keyof PageState>(key: K, fn: (prev: PageState[K]) => PageState[K]): void {
  update((prev) => {
    const nextSlice = fn(prev[key])
    if (nextSlice === prev[key]) return prev
    return { ...prev, [key]: nextSlice }
  })
}

// Convenience: update just the sites slice.
export function updateSites(fn: (prev: SitesSliceState) => SitesSliceState): void {
  updateSlice('sites', fn)
}
